import { createContext, useContext, useState, useCallback, useMemo, useRef, type ReactNode } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthProvider";
import { useCompany } from "@/contexts/CompanyContext";
import type {
  AdmAuthenticateResponse,
  AdmInstance,
  AdmDataLink,
  AdmDocumentContext,
} from "@/types/adm-document";

interface DocumentApiContextValue {
  documentApiUrl: string | undefined;
  getDocumentContext: () => Promise<AdmDocumentContext | null>;
  getAccessToken?: () => Promise<string | undefined>;
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
}

const noopContext: DocumentApiContextValue = {
  documentApiUrl: undefined,
  getDocumentContext: async () => null,
  getAccessToken: undefined,
  isInitialized: false,
  isInitializing: false,
  error: null,
};

const DocumentApiContext = createContext<DocumentApiContextValue>(noopContext);

export function useDocumentApi(): DocumentApiContextValue {
  return useContext(DocumentApiContext);
}

interface DocumentApiProviderProps {
  documentApiUrl: string | undefined;
  children: ReactNode;
}

interface InstanceDataLinks {
  instance: AdmInstance;
  dataLinks: AdmDataLink[];
}

export function DocumentApiProvider({ documentApiUrl, children }: DocumentApiProviderProps): React.ReactElement {
  const { getAccessToken } = useAuth();
  const { currentCompany } = useCompany();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const instancesCacheRef = useRef<AdmInstance[] | null>(null);
  const contextCacheRef = useRef<Map<string, AdmDocumentContext>>(new Map());

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (getAccessToken) {
      const token = await getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }, [getAccessToken]);

  const getAuthenticatedInstances = useCallback(async (): Promise<AdmInstance[]> => {
    if (instancesCacheRef.current) {
      return instancesCacheRef.current;
    }

    if (!documentApiUrl) {
      throw new Error("Document API URL not configured");
    }

    const headers = await getAuthHeaders();

    const authResponse = await fetch(`${documentApiUrl}/Security/AuthenticateAccessToken`, {
      method: "GET",
      headers,
    });

    if (!authResponse.ok) {
      throw new Error(`ADM authentication failed (HTTP ${authResponse.status})`);
    }

    const authData: AdmAuthenticateResponse = await authResponse.json();

    if (authData.Instances.length === 0) {
      throw new Error("No ADM instances found for this user");
    }

    instancesCacheRef.current = authData.Instances;
    return authData.Instances;
  }, [documentApiUrl, getAuthHeaders]);

  const fetchDataLinksForInstance = useCallback(
    async (instanceUrl: string): Promise<AdmDataLink[]> => {
      const headers = await getAuthHeaders();
      const response = await fetch(`${instanceUrl}/DataLinks`, {
        method: "GET",
        headers,
      });
      if (!response.ok) {
        return [];
      }
      return response.json();
    },
    [getAuthHeaders],
  );

  const resolveContextForCompany = useCallback(
    async (financialsInstanceId: string | undefined): Promise<AdmDocumentContext | null> => {
      if (financialsInstanceId && contextCacheRef.current.has(financialsInstanceId)) {
        return contextCacheRef.current.get(financialsInstanceId)!;
      }

      const instances = await getAuthenticatedInstances();

      // Match company's FinancialsInstanceId against ADM instance IDs —
      // only call DataLinks on the matched instance (avoids 500s on unrelated instances).
      if (financialsInstanceId) {
        const normalizedId = financialsInstanceId.replace(/-/g, "");

        const matchedInstance = instances.find(
          (inst) => inst.Id.replace(/-/g, "") === normalizedId,
        );

        if (matchedInstance) {
          const dataLinks = await fetchDataLinksForInstance(matchedInstance.Url);
          const dbName = dataLinks.length > 0 ? dataLinks[0].DatabaseName : "";
          const ctx: AdmDocumentContext = {
            instanceUrl: matchedInstance.Url,
            dbName,
          };
          contextCacheRef.current.set(financialsInstanceId, ctx);
          return ctx;
        }
      }

      // Fetch DataLinks for all authenticated instances once and reuse the results
      // for all subsequent matching/fallback logic (avoids redundant network calls).
      const instanceDataLinksMap: InstanceDataLinks[] = [];
      for (const instance of instances) {
        const dataLinks = await fetchDataLinksForInstance(instance.Url);
        instanceDataLinksMap.push({ instance, dataLinks });
      }

      // Try to match via DatabaseName/Name field against financialsInstanceId.
      if (financialsInstanceId) {
        for (const { instance, dataLinks } of instanceDataLinksMap) {
          const match = dataLinks.find(
            (dl) => dl.DatabaseName === financialsInstanceId || dl.Name === financialsInstanceId,
          );
          if (match) {
            const ctx: AdmDocumentContext = {
              instanceUrl: instance.Url,
              dbName: match.DatabaseName,
            };
            contextCacheRef.current.set(financialsInstanceId, ctx);
            return ctx;
          }
        }
      }

      // Fallback: use whichever instances returned valid DataLinks.
      const withDataLinks = instanceDataLinksMap.filter(({ dataLinks }) => dataLinks.length > 0);

      if (withDataLinks.length === 0) {
        toast.warning("No integration link found in Document system for this instance");
        return null;
      }

      if (withDataLinks.length === 1) {
        const { instance, dataLinks } = withDataLinks[0];
        const ctx: AdmDocumentContext = {
          instanceUrl: instance.Url,
          dbName: dataLinks[0].DatabaseName,
        };
        if (financialsInstanceId) {
          contextCacheRef.current.set(financialsInstanceId, ctx);
        }
        return ctx;
      }

      throw new Error(
        financialsInstanceId
          ? `No ADM instance found matching FinancialsInstanceId "${financialsInstanceId}"`
          : "Cannot resolve ADM instance — company has no linked Financials instance",
      );
    },
    [getAuthenticatedInstances, fetchDataLinksForInstance],
  );

  const getDocumentContext = useCallback(async (): Promise<AdmDocumentContext | null> => {
    const financialsInstanceId = currentCompany?.financialsInstanceId;

    if (!financialsInstanceId) {
      toast.warning("Company does not have a linked Financials instance configured");
      return null;
    }

    if (contextCacheRef.current.has(financialsInstanceId)) {
      return contextCacheRef.current.get(financialsInstanceId)!;
    }

    setIsInitializing(true);
    setError(null);

    try {
      const ctx = await resolveContextForCompany(financialsInstanceId);
      setIsInitialized(true);
      return ctx;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to initialize ADM";
      console.warn("[ADM] Document context initialization failed:", message);
      setError(message);
      return null;
    } finally {
      setIsInitializing(false);
    }
  }, [currentCompany?.financialsInstanceId, resolveContextForCompany]);

  const value = useMemo<DocumentApiContextValue>(() => ({
    documentApiUrl,
    getDocumentContext,
    getAccessToken,
    isInitialized,
    isInitializing,
    error,
  }), [documentApiUrl, getDocumentContext, getAccessToken, isInitialized, isInitializing, error]);

  return (
    <DocumentApiContext.Provider value={value}>
      {children}
    </DocumentApiContext.Provider>
  );
}
